import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, IconButton, Button,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Card, CardContent, Tooltip, InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Refresh as RefreshIcon,
  CreditCard as CreditCardIcon, Download as DownloadIcon,
  CheckCircle as CheckCircleIcon, Close as CloseIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';
import { usePageBreakpoints, pageContainerSx } from '../../hooks/useClinicPageBreakpoints';


const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

interface CardSettlement {
  id: string;
  card_brand: string;
  card_type: string;
  original_amount: number;
  fee_percentage: number;
  fee_amount: number;
  net_amount: number;
  installment_number: number;
  total_installments: number;
  expected_date: string;
  settlement_date: string | null;
  status: string;
  bank_account_id: string | null;
  notes: string;
}

interface Summary {
  total_pending: number;
  total_settled: number;
  total_fees: number;
}

const CARD_BRANDS = ['Visa', 'Mastercard', 'Elo', 'Amex', 'Hipercard'];
const CARD_TYPES = [
  { value: 'all', label: 'Todos' },
  { value: 'credit', label: 'Crédito' },
  { value: 'debit', label: 'Débito' },
];
const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'pending', label: 'Pendente' },
  { value: 'settled', label: 'Liquidado' },
  { value: 'partial', label: 'Parcial' },
];

const statusColor = (s: string): 'warning' | 'success' | 'info' | 'default' => {
  switch (s) {
    case 'pending': return 'warning';
    case 'settled': return 'success';
    case 'partial': return 'info';
    default: return 'default';
  }
};

const statusLabel = (s: string) => {
  switch (s) {
    case 'pending': return 'Pendente';
    case 'settled': return 'Liquidado';
    case 'partial': return 'Parcial';
    default: return s;
  }
};

const emptyForm = {
  card_brand: 'Visa',
  card_type: 'credit',
  original_amount: '',
  fee_percentage: '',
  fee_amount: '',
  net_amount: '',
  installment_number: '1',
  total_installments: '1',
  expected_date: '',
  bank_account_id: '',
  notes: '',
};

const CardSettlements: React.FC = () => {
  const { isMobile } = usePageBreakpoints();

  const [rows, setRows] = useState<CardSettlement[]>([]);
  const [summary, setSummary] = useState<Summary>({ total_pending: 0, total_settled: 0, total_fees: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const [filterBrand, setFilterBrand] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState<any>({ ...emptyForm });

  const [settleDialog, setSettleDialog] = useState(false);
  const [settleId, setSettleId] = useState('');
  const [settleDate, setSettleDate] = useState('');
  const [settleBankAccount, setSettleBankAccount] = useState('');

  const loadData = async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page: page + 1, limit };
      if (filterBrand !== 'all') params.card_brand = filterBrand;
      if (filterType !== 'all') params.card_type = filterType;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;

      const { data } = await axios.get(getApiUrl('/financial-module/card-settlements'), { params });
      const payload = data.data || data;
      setRows(payload.rows || payload.items || payload || []);
      setTotal(payload.total || 0);
      setSummary(payload.summary || { total_pending: 0, total_settled: 0, total_fees: 0 });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao carregar dados', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [page, limit]);

  const handleFilter = () => { setPage(0); loadData(); };

  const recalc = (field: string, value: string, current: any) => {
    const updated = { ...current, [field]: value };
    const original = parseFloat(updated.original_amount) || 0;
    const pct = parseFloat(updated.fee_percentage) || 0;
    updated.fee_amount = ((original * pct) / 100).toFixed(2);
    updated.net_amount = (original - parseFloat(updated.fee_amount)).toFixed(2);
    return updated;
  };

  const handleFormChange = (field: string, value: string) => {
    if (field === 'original_amount' || field === 'fee_percentage') {
      setForm((prev: any) => recalc(field, value, prev));
    } else {
      setForm((prev: any) => ({ ...prev, [field]: value }));
    }
  };

  const handleCreate = async () => {
    try {
      await axios.post(getApiUrl('/financial-module/card-settlements'), {
        ...form,
        original_amount: parseFloat(form.original_amount) || 0,
        fee_percentage: parseFloat(form.fee_percentage) || 0,
        fee_amount: parseFloat(form.fee_amount) || 0,
        net_amount: parseFloat(form.net_amount) || 0,
        installment_number: parseInt(form.installment_number) || 1,
        total_installments: parseInt(form.total_installments) || 1,
        bank_account_id: form.bank_account_id || null,
      });
      setSnackbar({ open: true, message: 'Lançamento criado com sucesso!', severity: 'success' });
      setOpenCreate(false);
      setForm({ ...emptyForm });
      loadData();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao criar lançamento', severity: 'error' });
    }
  };

  const handleSettle = async () => {
    try {
      await axios.post(getApiUrl(`/financial-module/card-settlements/${settleId}/settle`), {
        settlement_date: settleDate,
        bank_account_id: settleBankAccount || null,
      });
      setSnackbar({ open: true, message: 'Pagamento baixado com sucesso!', severity: 'success' });
      setSettleDialog(false);
      setSettleId('');
      setSettleDate('');
      setSettleBankAccount('');
      loadData();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao baixar pagamento', severity: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Deseja excluir este lançamento?')) return;
    try {
      await axios.delete(getApiUrl(`/financial-module/card-settlements/${id}`));
      setSnackbar({ open: true, message: 'Excluído com sucesso!', severity: 'success' });
      loadData();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Erro ao excluir', severity: 'error' });
    }
  };

  const openSettleDialog = (id: string) => {
    setSettleId(id);
    setSettleDate(new Date().toISOString().slice(0, 10));
    setSettleBankAccount('');
    setSettleDialog(true);
  };

  return (
    <Box sx={pageContainerSx}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CreditCardIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h5" fontWeight={700}>Baixar Pagamentos - Cartões</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadData}>Atualizar</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm({ ...emptyForm }); setOpenCreate(true); }}>
            Novo Lançamento
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Pendente', value: summary.total_pending, color: '#e65100', bg: '#fff3e0' },
          { label: 'Total Liquidado', value: summary.total_settled, color: '#2e7d32', bg: '#e8f5e9' },
          { label: 'Total Taxas', value: summary.total_fees, color: '#c62828', bg: '#ffebee' },
        ].map((c) => (
          <Grid item xs={12} sm={4} key={c.label}>
            <Card sx={{ backgroundColor: c.bg }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body2" color="text.secondary">{c.label}</Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: c.color }}>{formatCurrency(c.value)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Bandeira</InputLabel>
              <Select value={filterBrand} label="Bandeira" onChange={(e) => setFilterBrand(e.target.value)}>
                <MenuItem value="all">Todas</MenuItem>
                {CARD_BRANDS.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select value={filterType} label="Tipo" onChange={(e) => setFilterType(e.target.value)}>
                {CARD_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
                {STATUS_OPTIONS.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField fullWidth size="small" type="date" label="Data Início" value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField fullWidth size="small" type="date" label="Data Fim" value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button fullWidth variant="contained" onClick={handleFilter}>Filtrar</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, backgroundColor: '#f5f5f5' } }}>
                  <TableCell>Bandeira</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell align="right">Valor Original</TableCell>
                  <TableCell align="right">Taxa %</TableCell>
                  <TableCell align="right">Taxa R$</TableCell>
                  <TableCell align="right">Valor Líquido</TableCell>
                  <TableCell align="center">Parcela</TableCell>
                  <TableCell>Prev. Recebimento</TableCell>
                  <TableCell>Data Baixa</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">Nenhum lançamento encontrado</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{r.card_brand}</TableCell>
                      <TableCell>{r.card_type === 'credit' ? 'Crédito' : 'Débito'}</TableCell>
                      <TableCell align="right">{formatCurrency(r.original_amount)}</TableCell>
                      <TableCell align="right">{(r.fee_percentage || 0).toFixed(2)}%</TableCell>
                      <TableCell align="right">{formatCurrency(r.fee_amount)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(r.net_amount)}</TableCell>
                      <TableCell align="center">{r.installment_number}/{r.total_installments}</TableCell>
                      <TableCell>{r.expected_date ? new Date(r.expected_date).toLocaleDateString('pt-BR') : '-'}</TableCell>
                      <TableCell>{r.settlement_date ? new Date(r.settlement_date).toLocaleDateString('pt-BR') : '-'}</TableCell>
                      <TableCell align="center">
                        <Chip label={statusLabel(r.status)} color={statusColor(r.status)} size="small" />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          {r.status === 'pending' && (
                            <Tooltip title="Baixar">
                              <IconButton size="small" color="success" onClick={() => openSettleDialog(r.id)}>
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Excluir">
                            <IconButton size="small" color="error" onClick={() => handleDelete(r.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={limit}
          onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Linhas por página"
        />
      </Paper>

      {/* Create Dialog */}
      <Dialog fullScreen={isMobile} scroll="paper" open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Novo Lançamento de Cartão
          <IconButton onClick={() => setOpenCreate(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Bandeira</InputLabel>
                <Select value={form.card_brand} label="Bandeira" onChange={(e) => handleFormChange('card_brand', e.target.value)}>
                  {CARD_BRANDS.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select value={form.card_type} label="Tipo" onChange={(e) => handleFormChange('card_type', e.target.value)}>
                  <MenuItem value="credit">Crédito</MenuItem>
                  <MenuItem value="debit">Débito</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Valor Original" type="number" value={form.original_amount}
                onChange={(e) => handleFormChange('original_amount', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="Taxa %" type="number" value={form.fee_percentage}
                onChange={(e) => handleFormChange('fee_percentage', e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="Taxa R$" type="number" value={form.fee_amount} disabled
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
            </Grid>
            <Grid item xs={4}>
              <TextField fullWidth size="small" label="Valor Líquido" type="number" value={form.net_amount} disabled
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Nº Parcela" type="number" value={form.installment_number}
                onChange={(e) => handleFormChange('installment_number', e.target.value)} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Total Parcelas" type="number" value={form.total_installments}
                onChange={(e) => handleFormChange('total_installments', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" type="date" label="Previsão Recebimento" value={form.expected_date}
                onChange={(e) => handleFormChange('expected_date', e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Conta Bancária (ID)" value={form.bank_account_id}
                onChange={(e) => handleFormChange('bank_account_id', e.target.value)} placeholder="Opcional" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Observações" multiline rows={2} value={form.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!form.original_amount || !form.expected_date}>
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settle Dialog */}
      <Dialog fullScreen={isMobile} scroll="paper" open={settleDialog} onClose={() => setSettleDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Baixar Pagamento
          <IconButton onClick={() => setSettleDialog(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" type="date" label="Data da Baixa" value={settleDate}
                onChange={(e) => setSettleDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Conta Bancária (ID)" value={settleBankAccount}
                onChange={(e) => setSettleBankAccount(e.target.value)} placeholder="Opcional" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettleDialog(false)}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={handleSettle} startIcon={<CheckCircleIcon />}
            disabled={!settleDate}>
            Confirmar Baixa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CardSettlements;
